import { Box } from "@mui/joy";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import DarkCard from "../../common/DarkCard";
import DarkTypography from "../../common/DarkTypography";
import { colorTokens, textTokens } from "../../../styles/globalStyles";
import {
  statsChartAxisTick,
  statsChartTooltipLabelStyles,
  statsChartTooltipStyles,
} from "../statsChartStyles";
import type { StatsMonthlyChartConfig, StatsMonthRow } from "../statsPeriodHelpers";

const CHART_HEIGHT = { xs: 220, md: 260 };

type StatsMonthlyChartProps = {
  config: StatsMonthlyChartConfig;
  data: StatsMonthRow[];
};

export default function StatsMonthlyChart({ config, data }: StatsMonthlyChartProps) {
  return (
    <DarkCard sx={{ minWidth: 0 }}>
      <DarkTypography level="title-md" colorVariant="title" sx={{ mb: 1 }}>
        {config.title}
      </DarkTypography>
      <Box sx={{ width: "100%", height: CHART_HEIGHT }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 8, right: 4, left: -16, bottom: 0 }}>
            <CartesianGrid
              strokeDasharray="3 6"
              stroke={colorTokens.border.default}
              vertical={false}
            />
            <XAxis
              dataKey="month"
              tick={statsChartAxisTick}
              axisLine={{ stroke: colorTokens.border.default }}
              tickLine={false}
              interval={0}
              angle={-45}
              textAnchor="end"
              height={40}
            />
            <YAxis
              allowDecimals={false}
              tick={statsChartAxisTick}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              cursor={{ fill: colorTokens.bg.hover, opacity: 0.35 }}
              contentStyle={statsChartTooltipStyles}
              labelStyle={statsChartTooltipLabelStyles}
            />
            {config.series.length > 1 && (
              <Legend
                wrapperStyle={{
                  color: colorTokens.text.secondary,
                  fontFamily: textTokens.fontFamily,
                  fontSize: 12,
                }}
              />
            )}
            {config.series.map((serie) => (
              <Bar
                key={serie.key}
                dataKey={serie.key}
                name={serie.label}
                fill={serie.color}
                radius={[4, 4, 0, 0]}
                maxBarSize={18}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </DarkCard>
  );
}
